import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { endpoints } from '@rossum/api-client';
import { HttpError } from '@rossum/api-client/errors';
import { isEmbedded } from '../../../../constants/config';
import { api } from '../../../../lib/apiClient';
import { personalInfoPath } from '../../../personal-settings/routes';
import { notify } from '../../../toaster';
import { getTranslationError } from '../errorHandlers';

type UseTranslateDocumentProps = {
  annotationId?: number;
  targetLanguage?: string;
  sourceLanguage?: string;
  pageNumber: number;
  observeOnly?: boolean;
};

const TEN_MINUTES = 1000 * 60 * 10;
const GET_TRANSLATIONS_QUERY_KEY = 'get-translations';

export const useTranslateDocument = ({
  annotationId,
  targetLanguage,
  sourceLanguage,
  pageNumber,
  observeOnly = false,
}: UseTranslateDocumentProps) => {
  const intl = useIntl();
  return useQuery({
    queryKey: [
      GET_TRANSLATIONS_QUERY_KEY,
      targetLanguage,
      sourceLanguage,
      annotationId,
      pageNumber,
    ] as const,
    queryFn: async () => {
      if (!annotationId)
        throw new Error('Annotation id is needed to translate.');
      if (!targetLanguage)
        throw new Error(
          'Target language must be defined to translate the document'
        );

      return api
        .request(
          endpoints.annotations.translate(annotationId, {
            granularity: 'lines',
            pageNumbers: [pageNumber],
            targetLanguage,
            sourceLanguage,
          })
        )
        .catch(error => {
          if (error instanceof HttpError) {
            const { errorKey } = getTranslationError(error.data);

            if (targetLanguage && errorKey === 'invalidTargetLanguage') {
              const embedded = isEmbedded();
              notify.error({
                title: intl.formatMessage(
                  {
                    id: `components.documentValidation.translation.errors.invalidTargetLanguage${embedded ? '.embedded' : ''}`,
                  },
                  { targetLanguage }
                ),
                id: 'invalid_translation_target_language',
                toastActions: embedded
                  ? undefined
                  : [
                      {
                        label: intl.formatMessage({
                          id: 'components.documentValidation.translation.errors.invalidTargetLanguage.action',
                        }),
                        to: personalInfoPath(),
                        onClick: () => {},
                      },
                    ],
              });
            } else {
              notify.error({
                title: intl.formatMessage({
                  id: 'components.documentValidation.translation.errors.unknown',
                }),
                id: 'translation_error',
              });
            }
          }

          throw error;
        });
    },
    staleTime: TEN_MINUTES,
    keepPreviousData: !observeOnly,
    enabled: Boolean(annotationId && targetLanguage) && !observeOnly,
  });
};
